import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, ScanLine, ImagePlus, FileText, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TopBar } from "@/components/PageShell";
import ImageSlot from "@/components/ImageSlot";
import { usePaywall } from "@/hooks/usePaywall";

export default function DocumentReader() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const { authorize, PaywallElement } = usePaywall("OCR");

  const onFiles = (list) => setFiles(prev => [...prev, ...Array.from(list)]);

  const process = async () => {
    if (!files.length) { toast.error("Envie ao menos um documento."); return; }
    // ↓↓↓ Paywall gate: 1 unidade por arquivo (page ≈ file para MVP)
    const authorization = await authorize({ quantity: files.length });
    if (!authorization) { toast.info("Processamento cancelado."); return; }
    setLoading(true);
    try {
      const extractions = [];
      for (let i = 0; i < files.length; i++) {
        setProgress(`Processando ${i+1}/${files.length}: ${files[i].name}`);
        const fd = new FormData();
        fd.append("file", files[i]);
        const r = await api.post("/ocr/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
        extractions.push(r.data);
      }
      // commit usage após sucesso
      await authorization.commit();
      const merged = { fields: {}, document_types: [], ids: [], previews: [] };
      for (const e of extractions) {
        merged.document_types.push(e.document_type);
        merged.ids.push(e.id);
        merged.previews.push({ id: e.id, name: e.filename, type: e.document_type });
        for (const [k, v] of Object.entries(e.fields || {})) {
          if (!merged.fields[k] || (v.confidence || 0) > (merged.fields[k].confidence || 0)) {
            merged.fields[k] = v;
          }
        }
      }
      sessionStorage.setItem("ocr_review", JSON.stringify(merged));
      navigate("/review");
    } catch (e) {
      // rollback reserva (não consome franquia nem debita)
      await authorization.release();
      console.error(e);
      toast.error(e?.response?.data?.detail || "Erro ao processar OCR");
    } finally {
      setLoading(false); setProgress("");
    }
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="reader-shell">
      <TopBar/>
      <div className="p-6 lg:p-8 w-full font-sans">
      {/* Hero — imagem 4K institucional (slot: reader_hero) */}
      <section className="relative rounded-2xl overflow-hidden border h-[340px] mb-6" style={{ borderColor: "#E7E5E2" }} data-testid="reader-hero">
        <ImageSlot slot="reader_hero" alt="Leitor de Documentos · biblioteca clássica" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(11,27,47,0.82) 0%, rgba(11,27,47,0.55) 40%, rgba(11,27,47,0.12) 78%, rgba(11,27,47,0) 100%)" }}/>
        <div className="relative h-full flex flex-col justify-center px-8 md:px-12 max-w-2xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl inline-flex items-center justify-center backdrop-blur" style={{ background: "rgba(240,211,122,0.18)", border: "1px solid rgba(240,211,122,0.55)" }}>
              <ScanLine className="w-5 h-5" style={{ color: "#F0D37A" }}/>
            </div>
            <div className="text-[11px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#F0D37A" }}>
              HERMES · OCR
            </div>
          </div>
          <h1 className="font-serif text-5xl md:text-6xl leading-[1.02] tracking-tight text-white" style={{ textShadow: "0 2px 24px rgba(0,0,0,0.4)" }}>
            Leitor de <span className="italic" style={{ color: "#F5DE8C" }}>Documentos</span>
          </h1>
          <p className="mt-4 text-base text-white/90 max-w-xl" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.45)" }}>
            Envie RG, CNH, CIN e/ou comprovante de residência. Aceita JPG, PNG ou PDF.
          </p>
        </div>
      </section>

      <div className="bg-white rounded-2xl border p-6" style={{ borderColor: "#E7E5E2" }}>
        {/* Dropzone com lupa: a lente (centro-esquerda) recebe o texto de instrução */}
        <label htmlFor="file-input"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
          data-testid="dropzone"
          className="relative block w-full max-w-[1180px] mx-auto cursor-pointer group"
          style={{ aspectRatio: "1536 / 1024" }}
        >
          {/* Lupa (asset, também disponível via /admin/images slot ocr_lupa) */}
          <ImageSlot
            slot="ocr_lupa"
            fallback="/lupa.png"
            alt="Solte seus documentos aqui"
            className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none transition-transform duration-500 group-hover:scale-[1.02]"
          />
          {/* Texto dentro da lente — coordenadas alinhadas ao centro visual do vidro */}
          <div
            className="absolute flex flex-col items-center justify-center text-center px-3"
            style={{ left: "9.5%", top: "10.5%", width: "39%", height: "56.5%" }}
          >
            <ImagePlus className="w-10 h-10 md:w-12 md:h-12 mb-3" style={{ color: "#0E1D2D" }}/>
            <div className="font-serif text-[20px] md:text-[26px] font-semibold text-[#0E1D2D] leading-tight">
              Solte os arquivos aqui
            </div>
            <div className="text-[13px] md:text-[15px] text-slate-700 mt-1.5 leading-snug max-w-[26ch]">
              ou clique para selecionar. Você pode enviar múltiplas páginas.
            </div>
            {files.length > 0 && (
              <div className="mt-3 text-[11px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100/90 rounded-full px-3 py-1">
                {files.length} arquivo{files.length===1?"":"s"} pronto{files.length===1?"":"s"}
              </div>
            )}
          </div>
          <input id="file-input" type="file" accept="image/*,application/pdf" multiple hidden
            onChange={(e) => onFiles(e.target.files)} data-testid="file-input" />
        </label>

        <div className="mt-4 flex flex-wrap items-center gap-3 justify-center">
          <label htmlFor="camera-input"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm cursor-pointer transition"
            data-testid="btn-camera-label">
            <Camera className="w-4 h-4" /> Tirar foto (câmera)
            <input id="camera-input" type="file" accept="image/*" capture="environment" hidden
              onChange={(e) => onFiles(e.target.files)} data-testid="camera-input" />
          </label>
          <span className="text-xs text-slate-400">No celular abrirá a câmera automaticamente.</span>
        </div>

        {files.length > 0 && (
          <ul className="mt-6 divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden">
            {files.map((f, i) => (
              <li key={i} className="flex items-center justify-between p-3 bg-white">
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-slate-500" />
                  <div>
                    <div className="text-sm text-slate-900">{f.name}</div>
                    <div className="text-xs text-slate-500">{(f.size/1024).toFixed(1)} KB</div>
                  </div>
                </div>
                <button onClick={() => setFiles(files.filter((_,idx) => idx !== i))}
                  className="text-xs text-slate-400 hover:text-red-600">Remover</button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-slate-500 flex items-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {progress || "OCR + IA extraem os dados automaticamente."}
          </div>
          <button data-testid="btn-process-ocr" disabled={loading || !files.length} onClick={process}
            className="gradient-primary text-white px-6 h-11 rounded-xl inline-flex items-center gap-2 hover:-translate-y-0.5 transition text-sm font-medium disabled:opacity-60">
            <ScanLine className="w-4 h-4" /> {loading ? "Processando..." : "Extrair dados"}
          </button>
        </div>
      </div>
      </div>
      {PaywallElement}
    </div>
  );
}
